// eslint-disable-next-line import/no-extraneous-dependencies
import React from 'react'
import {
    mapPercentileNames,
    flipGoodTypesBatter,
    flipGoodTypesPitcher,
} from 'bam-node-baseball-savant/apps/utils/possible-comp-tool-stats.ts'
import StatSlider from './StatSlider.tsx'
import PlayerHeader from './PlayerHeader.tsx'
import {
    PlayerComponentProps,
    SummaryStatRow,
    PlayerRow,
} from './utils/types.ts'
import { getFakePercentile } from './utils/helpers.ts'

import {
    LINE_HEIGHT,
    SECTION_MARGIN,
    HEADER_TEXT_HEIGHT,
    TEAM_LOGO_SIZE,
} from './utils/constants.ts'
import { useDimensions } from './utils/responsive-dimensions.tsx'

function PlayerComponent(props: PlayerComponentProps) {
    const selectedData = {} as Record<
        string,
        string | number | number[] | null | undefined
    >
    const selectedValues = {} as Record<string, number | null>
    const {
        selects,
        playerData,
        groupedSelects,
        existingTypes,
        maxSliderWidth,
        showValues,
        showTeams,
        showWinner,
        isNarrow,
        winners,
        summaryStats,
        currentType,
        winnerStats,
        isHidden,
    } = props

    const { TOP_BUFFER } = useDimensions()

    function getFakeRank(key: keyof PlayerRow, value: number | null) {
        const { year, grouping_code } = playerData

        const strippedName = mapPercentileNames(key)
        const thisYearSummary = summaryStats.find(
            (dat) =>
                Number(dat.year) === Number(year) &&
                dat.grouping_code === grouping_code
        )

        if (!thisYearSummary) {
            return null
        }

        const avg = Number(
            thisYearSummary[`avg_${strippedName}` as keyof SummaryStatRow]
        )

        const stdev = Number(
            thisYearSummary[`stdev_${strippedName}` as keyof SummaryStatRow]
        )

        const invert =
            (flipGoodTypesBatter.includes(key) && currentType === 'batter') ||
            (flipGoodTypesPitcher.includes(key) && currentType === 'pitcher')

        return getFakePercentile(Number(value), avg, stdev, invert)
    }

    selects.forEach((select) => {
        selectedData[select] = playerData[select]
        const valColumn = mapPercentileNames(select)
        selectedValues[select] =
            playerData[valColumn] === null
                ? null
                : Number(playerData[valColumn])
    })

    let numStatsSeen = 0

    const componentGroupings = existingTypes.map((type, groupingsSeen) => {
        const statSliders = groupedSelects[type].map((key, i) => {
            const rank = selectedData[key]
            const statWinner = winners[key]
            const value = selectedValues[key]

            const isFake = rank === null && value !== null
            const fakeRank = isFake ? getFakeRank(key, value) : null

            let percentileToUse = null

            if (rank !== null) {
                percentileToUse = Number(rank)
            } else if (isFake) {
                percentileToUse = fakeRank
            }

            return (
                <g
                    key={`${key}_${playerData.id}`}
                    transform={`translate(0, ${i * LINE_HEIGHT})`}
                >
                    <StatSlider
                        key={`${key}_${playerData.id}_slider`}
                        statName={key}
                        percentile={percentileToUse}
                        value={value}
                        maxSliderWidth={maxSliderWidth}
                        showValues={showValues}
                        showWinner={showWinner || winnerStats?.includes(key)}
                        isNarrow={isNarrow}
                        isWinner={playerData.uniqueId === statWinner}
                        isFake={isFake}
                        uniqueId={String(playerData.uniqueId)}
                    />
                </g>
            )
        })

        const headersOffset =
            (groupingsSeen + 1) * SECTION_MARGIN +
            (groupingsSeen + 1) * HEADER_TEXT_HEIGHT
        const statsOffset = numStatsSeen * LINE_HEIGHT
        const teamOffset = showTeams ? 0 : -TEAM_LOGO_SIZE

        const slidersGroup = (
            <g
                transform={`translate(0,${
                    headersOffset + statsOffset + TOP_BUFFER + teamOffset
                })`}
                key={`${type}_slider_grouping`}
            >
                {statSliders}
            </g>
        )

        numStatsSeen += groupedSelects[type].length

        return slidersGroup
    })

    return (
        <>
            <PlayerHeader
                data={playerData}
                maxSliderWidth={maxSliderWidth}
                showTeams={showTeams}
                isHidden={isHidden}
            />
            <g className={`player-wrapper-${playerData.id}`}>
                {componentGroupings}
            </g>
        </>
    )
}

export default PlayerComponent
